import { useMemo } from "react";
import { Button, Stack, Text, Tooltip } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import type { RecallCountdownPhase } from "../../hooks/useReportDeliveryRecall";

interface RecallReportButtonProps {
  remainingSeconds: number | null;
  countdownPhase: RecallCountdownPhase;
  methods: string[];
  scheduledAt: string | null;
  recipientCount: number;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  size?: "xs" | "sm" | "md";
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return "time unavailable";
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(
    2,
    "0"
  )}s`;
}

export function formatRecallScheduledTime(
  scheduledAt: string | null,
  timeZone?: string
): string {
  if (!scheduledAt) return "Time unavailable";
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
    timeZone,
  }).format(new Date(scheduledAt));
}

export function RecallReportButton({
  remainingSeconds,
  countdownPhase,
  methods,
  scheduledAt,
  recipientCount,
  onClick,
  disabled = false,
  loading = false,
  size = "sm",
}: RecallReportButtonProps) {
  const duration = useMemo(
    () => formatDuration(remainingSeconds),
    [remainingSeconds]
  );
  const buttonLabel = useMemo(() => {
    if (countdownPhase === "checking") return "Recall Report (checking...)";
    if (countdownPhase === "due_now") return "Recall Report (due now)";
    if (countdownPhase === "unavailable") {
      return "Recall Report (time unavailable)";
    }
    return `Recall Report (${duration})`;
  }, [countdownPhase, duration]);
  const methodSummary = useMemo(
    () => (methods.length > 0 ? methods.join(" + ") : "Method unavailable"),
    [methods]
  );
  const scheduleSummary = useMemo(
    () => formatRecallScheduledTime(scheduledAt),
    [scheduledAt]
  );
  const recipientSummary = useMemo(
    () => `${recipientCount} recipient${recipientCount === 1 ? "" : "s"}`,
    [recipientCount]
  );

  return (
    <Tooltip
      multiline
      withArrow
      withinPortal
      position="bottom"
      offset={8}
      events={{ hover: true, focus: true, touch: false }}
      label={
        <Stack gap={2} py={2}>
          <Text size="xs" fw={700}>
            REPORT QUEUED
          </Text>
          <Text size="xs">{methodSummary}</Text>
          <Text size="xs">Sends at {scheduleSummary}</Text>
          <Text size="xs" aria-live="off">
            {duration} remaining
          </Text>
          <Text size="xs">{recipientSummary}</Text>
        </Stack>
      }
    >
      <Button
        variant="default"
        size={size}
        leftSection={<IconAlertTriangle size={16} aria-hidden="true" />}
        onClick={onClick}
        disabled={disabled}
        loading={loading}
        aria-label={`${buttonLabel}. ${methodSummary}. Sends at ${scheduleSummary}. ${duration} remaining. ${recipientSummary}.`}
        style={{
          minWidth: 190,
          minHeight: 44,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        <span aria-live="off">{buttonLabel}</span>
      </Button>
    </Tooltip>
  );
}
